// The audio strip of the /transcripts/$id canvas (docs/09a): play / pause,
// the waveform, the two time chips — nothing else. Conversation bubbles and
// results-rail evidence links seek it through the `seekRequest` controlled
// prop; audio evidence is reached from there, never from a track under it.

import type { components } from "../api/schema";
import { captureFrontendWarning } from "../lib/sentry";
import {
  AudioPlayer,
  type AudioPlayerSeekRequest,
  EmptyState,
  Inline,
  InlineError,
  Spinner,
  Text,
} from "../ui";
import type { Assertion } from "./markerAuthoringDraft";

type MarkerResponse = components["schemas"]["MarkerResponse"];
type TranscriptResponse = components["schemas"]["TranscriptResponse"];

function reportDecodeFallback(): void {
  captureFrontendWarning("audio_peaks_fallback");
}

// Per-marker facts the results rail needs: the human display name, the
// current revision's assertion (verdict precondition, and the grammar a human
// Label's verdict is derived from), and the built-in key.
export interface MarkerDisplay {
  assertion: Assertion | null;
  builtinKey: string | null;
  name: string | null;
}

export interface TranscriptAudioProps {
  audioError?: string | null;
  audioIsPending?: boolean;
  audioPeaks?: number[] | null;
  audioSrc?: string | null;
  onPlayingChange?: (playing: boolean) => void;
  onTimeUpdate?: (ms: number) => void;
  seekRequest?: AudioPlayerSeekRequest | null;
  transcript: TranscriptResponse;
}

// No audio: renders nothing (text transcripts carry no empty audio region).
export function TranscriptAudio({
  audioError = null,
  audioIsPending = false,
  audioPeaks = null,
  audioSrc = null,
  onPlayingChange,
  onTimeUpdate,
  seekRequest = null,
  transcript,
}: TranscriptAudioProps) {
  if (!transcript.audio_uri) {
    return null;
  }

  return (
    <>
      {audioIsPending ? (
        <Inline gap={2}>
          <Spinner label="Loading audio" />
          <Text variant="muted">Loading audio...</Text>
        </Inline>
      ) : null}
      {audioError ? <InlineError>{audioError}</InlineError> : null}
      {audioSrc ? (
        <AudioPlayer
          decodeFallbackEnabled={!audioIsPending}
          onDecodeFallback={reportDecodeFallback}
          onPlayingChange={onPlayingChange}
          onTimeUpdate={onTimeUpdate}
          peaks={audioPeaks ?? undefined}
          seekRequest={seekRequest}
          src={audioSrc}
        />
      ) : null}
      {!audioIsPending && !audioError && !audioSrc ? (
        <EmptyState title="No audio available" />
      ) : null}
    </>
  );
}

// Per-marker display facts: the human name (current revision's display name,
// falling back to builtin_key), the revision's assertion, and the built-in key.
export function markerDisplayById(
  markers: MarkerResponse[] | undefined,
): Record<string, MarkerDisplay> {
  const displayById: Record<string, MarkerDisplay> = {};
  for (const marker of markers ?? []) {
    displayById[marker.id] = {
      assertion: marker.current_revision.assertion,
      builtinKey: marker.builtin_key,
      name: marker.current_revision.display_name || marker.builtin_key,
    };
  }
  return displayById;
}
